import React from 'react';
import {
  Box,
  CardBody,
  Link as DSLink,
  Typography,
} from '@saviynt/design-system';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import type { RoleAction, RoleCondition } from '../../../../services';
import type { RootState } from '../../../../services/types';

import { type MarkerConfig, parseMarkersToJSX } from '../../../../shared/utils';
import { useInvertedLocalize } from '../../../../utils/localization';

import roleCardConditionMessages from './messages';

import './RoleCardCondition.css';

// Unicode characters used as invisible markers for styled formatting
// These are zero-width characters that won't display if accidentally rendered
const FIELD_START = '\u200B'; // Zero-width space - marks field names for styling
const FIELD_END = '\u200C'; // Zero-width non-joiner - closes field name marker
const OPERATION_START = '\u200D'; // Zero-width joiner - marks operators for styling
const OPERATION_END = '\uFEFF'; // Zero-width no-break space - closes operator marker
const VALUE_START = '\u2060'; // Word joiner - marks quoted values for styling
const VALUE_END = '\u2061'; // Function application - closes value marker

// Marker configuration for parsing styled condition strings
// These are defined outside the component to avoid React component definition warnings
const conditionMarkerConfigs: MarkerConfig[] = [
  {
    start: FIELD_START,
    end: FIELD_END,
    createElement: (content, index) =>
      React.createElement(
        'span',
        {
          key: index,
          className: 'Roles-RoleCardCondition-fieldName',
        },
        content
      ),
  },
  {
    start: OPERATION_START,
    end: OPERATION_END,
    createElement: (content, index) =>
      React.createElement(
        'span',
        {
          key: index,
          className: 'Roles-RoleCardCondition-operation',
        },
        content
      ),
  },
  {
    start: VALUE_START,
    end: VALUE_END,
    createElement: (content, index) =>
      React.createElement(
        'span',
        {
          key: index,
          className: 'Roles-RoleCardCondition-conditionValue',
        },
        content
      ),
  },
];

// Constants for entitlements display
const MAX_ENT_LINE_CHARS = 100;
const ENT_SEPARATOR = '|';
const MAX_ENT_ROWS = 5;

// Interface for structured entitlement group data
interface EntitlementGroup {
  appName: string;
  entitlements: string[];
  fullText: string; // For character counting in line breaking
}

interface RoleCardConditionProps {
  conditions: RoleCondition[];
  actions?: RoleAction[]; // actions to display entitlements
  roleId: number;
}

/**
 * RoleCardCondition component displaying role conditions
 *
 * @param props - The component props
 * @returns JSX element containing the role conditions
 */
function RoleCardCondition({
  conditions,
  actions = [],
  roleId,
}: RoleCardConditionProps) {
  const l = useInvertedLocalize(roleCardConditionMessages);

  // Get user's locale from Redux for proper grammar rules in generated descriptions
  // Falls back to 'en' if not set or empty
  const userLocale = useSelector((state: RootState) => {
    const locale = state.user.userInfo?.userSavRoleMap?.locale;

    return locale && locale.length > 0 ? locale : 'en';
  });

  // Operator mapping - duplicated from RoleConditions component for consistency
  const getDisplayOperator = (operator: string) => {
    const operatorMap = {
      IN: '=', // 'is one of' → '='
      EQUALS: '=', // 'is' → '='
    };

    return operatorMap[operator as keyof typeof operatorMap] || operator;
  };

  // Render user conditions using ICU-compliant Intl.ListFormat with invisible markers for styling
  // Format: Field1 = 'Value1' and Field2 = 'Value2'
  // Uses invisible Unicode markers to preserve styling while allowing ICU formatting
  const renderConditions = (roleConditions: RoleCondition[]) => {
    if (roleConditions.length === 0) {
      return null;
    }

    // Create conjunction formatter for locale-aware "and" formatting
    const conjunctionFormatter = new Intl.ListFormat(userLocale, {
      type: 'conjunction',
    });

    // Build each condition as a string with markers wrapped around styled parts
    // Values are comma-separated (no disjunction formatter) and wrapped in quotes
    const conditionStrings = roleConditions.map((condition) => {
      const values = Array.isArray(condition.objectValue)
        ? condition.objectValue
        : [condition.objectValue];

      // Wrap field name with FIELD markers
      const fieldName = `${FIELD_START}${condition.objectName}${FIELD_END}`;

      // Wrap operator with OPERATION markers
      const operator = `${OPERATION_START}${getDisplayOperator(
        condition.operation
      )}${OPERATION_END}`;

      // Build values string with quotes and VALUE markers around each value
      // Keep comma separation (no disjunction formatter)
      const valuesStr = values
        .map((value) => `${VALUE_START}'${value}'${VALUE_END}`)
        .join(', ');

      // Combine into condition string: "FieldName = 'Value1', 'Value2'"
      return `${fieldName} ${operator} ${valuesStr}`;
    });

    // Use Intl.ListFormat to combine conditions with locale-appropriate "and" grammar
    const formattedConditions = conjunctionFormatter.format(conditionStrings);

    // Post-process: convert invisible markers to JSX with styled spans
    const jsxContent = parseMarkersToJSX(
      formattedConditions,
      conditionMarkerConfigs
    );

    return (
      <Typography
        kind='body2'
        className='Roles-RoleCardCondition-conditionLine'>
        {jsxContent}
      </Typography>
    );
  };

  // Process entitlements into structured group data for line breaking and rendering
  // Returns array of EntitlementGroup objects with app names, entitlements, and full text
  const processEntitlementGroups = (
    roleActions: RoleAction[]
  ): EntitlementGroup[] => {
    const groups = new Map<string, string[]>();

    // Group entitlements by application/endpoint
    roleActions.forEach((action) => {
      const { endpointDisplayName } = action.entitlementMetaData.endpoint;
      const { endpointName } = action.entitlementMetaData.endpoint;

      const appName =
        endpointDisplayName && endpointDisplayName !== endpointName
          ? endpointDisplayName
          : endpointName;

      // Use evDisplayName as primary choice (designed for UI display)
      // Fallback to entitlementValueName if evDisplayName is missing
      const entitlementName =
        action.entitlementMetaData.evDisplayName ||
        action.entitlementMetaData.entitlementValueName ||
        '';

      if (!groups.has(appName)) {
        groups.set(appName, []);
      }

      groups.get(appName)?.push(entitlementName);
    });

    // Convert groups to structured data with full text for character counting
    return Array.from(groups.entries()).map(([appName, entitlements]) => ({
      appName,
      entitlements,
      fullText: `${appName} → ${entitlements.join(', ')}`, // Used for character counting
    }));
  };

  // Break entitlement groups into lines based on character limit
  // Breaks only after separator characters to maintain visual consistency
  // Now works with structured EntitlementGroup data for better rendering control
  const breakIntoLines = (
    groupData: EntitlementGroup[]
  ): EntitlementGroup[][] => {
    const lines: EntitlementGroup[][] = []; // Array of arrays of groups per line
    let currentLineGroups: EntitlementGroup[] = [];
    let currentLineLength = 0;

    groupData.forEach((group, index) => {
      // Add separator before each group except the first
      const separatorLength = index === 0 ? 0 : ` ${ENT_SEPARATOR} `.length;
      const potentialLength =
        currentLineLength + separatorLength + group.fullText.length;

      // If adding this group would exceed the character limit, start a new line
      if (
        currentLineGroups.length > 0 &&
        potentialLength > MAX_ENT_LINE_CHARS
      ) {
        lines.push([...currentLineGroups]); // End current line with current groups
        currentLineGroups = [group]; // Start new line with current group (no separator prefix)
        currentLineLength = group.fullText.length;
      } else {
        currentLineGroups.push(group);
        currentLineLength = potentialLength;
      }
    });

    // Add the final line (contains remaining groups)
    if (currentLineGroups.length > 0) {
      lines.push(currentLineGroups);
    }

    return lines;
  };

  // Render entitlements with character-based line breaking and proper indentation
  // Format: Entitlements = APP1 → ent1, ent2 | APP2 → ent3, ent4
  const renderEntitlements = (roleActions: RoleAction[]) => {
    if (roleActions.length === 0) {
      return null;
    }

    const groupData = processEntitlementGroups(roleActions);
    const lines = breakIntoLines(groupData);

    return (
      <Box className='Roles-RoleCardCondition-entitlementsWrapper'>
        <Typography
          kind='body2'
          className='Roles-RoleCardCondition-entitlementsLabel'>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {`${l('Entitlements')} =`}
        </Typography>
        <Box className='Roles-RoleCardCondition-entitlementsContent'>
          {lines.slice(0, MAX_ENT_ROWS).map((lineGroups) => (
            <Typography
              key={lineGroups.map((g) => g.appName).join('-')}
              kind='body2'
              lineLimit={3}
              tooltipConfigs={{
                text: lineGroups
                  .map((g) => g.fullText)
                  .join(` ${ENT_SEPARATOR} `),
                placement: 'top',
              }}
              className='Roles-RoleCardCondition-entitlementsLine'>
              {lineGroups.map((group, groupIndex) => (
                <React.Fragment key={group.appName}>
                  {groupIndex > 0 && (
                    <span className='Roles-RoleCardCondition-entitlementSeparator'>
                      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                      {` ${ENT_SEPARATOR} `}
                    </span>
                  )}
                  <span className='Roles-RoleCardCondition-applicationName'>
                    {group.appName}
                  </span>
                  <span className='Roles-RoleCardCondition-entitlementArrow'>
                    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                    &nbsp;&rarr;&nbsp;
                  </span>
                  <span className='Roles-RoleCardCondition-entitlementList'>
                    {group.entitlements.join(', ')}
                  </span>
                </React.Fragment>
              ))}
            </Typography>
          ))}
          {lines.length > MAX_ENT_ROWS && (
            <DSLink
              RouterComponent={RouterLink}
              href={`/role-details/${roleId}`}
              text={l('More...')}
              size='medium'
              color='brand'
              target='_self'
              className='Roles-RoleCardCondition-entitlementsMore'
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <CardBody className='Roles-RoleCardCondition'>
      <Box className='Roles-RoleCardCondition-content'>
        <Typography
          kind='h4'
          className='Roles-RoleCardCondition-title'
          color='--color-foreground-neutral-bold'>
          {l('Role Condition')}
        </Typography>

        <Box className='Roles-RoleCardCondition-conditions'>
          {/* Render user conditions on single line with 'and' separators */}
          {renderConditions(conditions)}

          {/* Render entitlements grouped by application */}
          {renderEntitlements(actions)}
        </Box>
      </Box>
    </CardBody>
  );
}

RoleCardCondition.defaultProps = {
  actions: [],
};

export default RoleCardCondition;
